export type InputPopoverSide = "above" | "below";
export type InputPopoverAlign = "start" | "end" | "center" | "stretch";

export interface InputPopoverRect {
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  width: number;
}

interface InputPopoverPlacementOptions {
  align?: InputPopoverAlign;
  anchor: InputPopoverRect;
  gap?: number;
  inset?: number;
  minHeight?: number;
  popover: Pick<InputPopoverRect, "height" | "width">;
  preferredSide?: InputPopoverSide;
  viewport: Omit<InputPopoverRect, "height" | "width">;
}

export interface InputPopoverPlacementResult {
  left: number;
  maxHeight: number;
  maxWidth: number;
  side: InputPopoverSide;
  top: number;
  width?: number;
}

const DEFAULT_GAP = 6;
const DEFAULT_INSET = 12;
const DEFAULT_MIN_HEIGHT = 80;

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

function chooseSide({
  popoverHeight,
  preferredSide,
  spaceAbove,
  spaceBelow,
}: {
  popoverHeight: number;
  preferredSide: InputPopoverSide;
  spaceAbove: number;
  spaceBelow: number;
}): InputPopoverSide {
  if (preferredSide === "above") {
    return spaceAbove >= popoverHeight || spaceAbove >= spaceBelow
      ? "above"
      : "below";
  }
  return spaceBelow >= popoverHeight || spaceBelow >= spaceAbove
    ? "below"
    : "above";
}

export function computeInputPopoverPlacement({
  align = "start",
  anchor,
  gap = DEFAULT_GAP,
  inset = DEFAULT_INSET,
  minHeight = DEFAULT_MIN_HEIGHT,
  popover,
  preferredSide = "above",
  viewport,
}: InputPopoverPlacementOptions): InputPopoverPlacementResult {
  const viewportWidth = Math.max(0, viewport.right - viewport.left - inset * 2);
  const viewportHeight = Math.max(
    0,
    viewport.bottom - viewport.top - inset * 2,
  );
  const width =
    align === "stretch"
      ? Math.min(anchor.width, viewportWidth)
      : Math.min(popover.width, viewportWidth);
  const spaceAbove = Math.max(0, anchor.top - viewport.top - inset - gap);
  const spaceBelow = Math.max(0, viewport.bottom - inset - anchor.bottom - gap);
  const side = chooseSide({
    popoverHeight: popover.height,
    preferredSide,
    spaceAbove,
    spaceBelow,
  });
  const sideSpace = side === "above" ? spaceAbove : spaceBelow;
  const maxHeight = Math.max(
    0,
    Math.min(Math.max(minHeight, sideSpace), viewportHeight),
  );
  const height = Math.min(popover.height, maxHeight);

  const rawTop =
    side === "above" ? anchor.top - gap - height : anchor.bottom + gap;
  const top = clamp(
    rawTop,
    viewport.top + inset,
    viewport.bottom - inset - height,
  );

  let rawLeft: number;
  if (align === "end") {
    rawLeft = anchor.right - width;
  } else if (align === "center") {
    rawLeft = anchor.left + (anchor.width - width) / 2;
  } else {
    rawLeft = anchor.left;
  }

  return {
    left: clamp(rawLeft, viewport.left + inset, viewport.right - inset - width),
    maxHeight,
    maxWidth: viewportWidth,
    side,
    top,
    width: align === "stretch" ? width : undefined,
  };
}
